import React, { useState, useCallback, Dispatch, SetStateAction, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@reach/router';
import queryString from 'query-string';

import { useReCaptcha } from 'hooks/context/ReCaptcha';
import { RecaptchaLocaleCode } from 'hooks/context/ReCaptcha.util';
import { useCart } from 'hooks/context/cart';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { useCustomer } from 'hooks/context/customer/customer';
import { useMerchant } from 'hooks/context/merchant';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useServiceType } from 'hooks/context/serviceType';
import { useDelivery } from 'hooks/context/Delivery';
import { useDebounce } from '@clover-util/use-debounce';
import { useSafeContext, createNamedContext } from '@clover-util/react-context';
import { useApplePay } from 'components/ApplePay/ApplePay';
import { useInvoiceSummary } from '../InvoiceSummary';
import { usePostOrdersRequest, usePutTableOrders, useTableOrderPaymentsRequest } from 'hooks/useApi';
import { CLIENT_PATH } from 'olo-service/axiosInstance';
import { Customer, GiftCardItem, OrderReceipt } from 'olo-service/models';
import { OLO_IPG_TOKEN, isPaymentProcessorIpg } from 'util/IpgPay';
import { OLO_APPLE_PAY_TOKEN } from 'components/ApplePay/ApplePayIdentifierUtil';
import { useOrderResponseSummary } from '../OrderResponseSummary';
import { COLO_CONTACT_INFO, COLO_CONTACT_NOTE, useOrderDetails } from '../OrderDetails';
import { IpgPaymentForm } from './IpgPaymentForm';
import { useCreateApplePaySession } from 'components/ApplePay/useCreateApplePaySession';
import { getI18nKeyFromError, GYFT_ERROR_CODE, parseErrorMessage, reCaptchaReadyLogger } from './utils';
import { invalidateSession } from 'util/ErrorHandling/errorHandling.util';
import { useMerchantState } from 'hooks/context/MerchantState';

// eslint-disable-next-line no-restricted-imports
import type { DebouncedFunc } from 'lodash';
import { SessionStorage } from 'util/Storage';
import { useScheduledOrderDisplay } from 'hooks/context/ScheduledOrderDisplay';
import { useScheduledOrderMethods } from 'hooks/context/ScheduledOrderMethods';
import { formatTimeForDelivery, timeFormat } from 'hooks/context/Delivery.util';
import { heapTrack } from 'util/Analytics';
import { useGiftCard } from '../GiftCard/GiftCardContext';
import { OnlineOrderProviders } from 'hooks/useApi/usePostOrdersRequest';
import { QUERY_PARAMS } from 'components/LandingPage.const';

type StoredState = {
  error?: string;
  errorMessage?: string;
  isOrderProcessing: boolean;
}

export type SubmitOrderState = StoredState & {
  setState: Dispatch<SetStateAction<StoredState>>;
  isReCaptchaReady: boolean;
  reCaptchaLocale: RecaptchaLocaleCode;
  hasUnrecoverableError: boolean;
  submit: DebouncedFunc<(totalwithTip?:number) => Promise<void>>;
  submitWithApplePay: () => Promise<void>;
  submitWithGooglePay: DebouncedFunc<(token: Clover.HasToken) => Promise<void>>;
  submitTableOrder: () => Promise<void>;
};

const SubmitOrderContext = createNamedContext<SubmitOrderState>('SubmitOrderContext');

export function useSubmitOrder() {
  return useSafeContext(useSubmitOrder, SubmitOrderContext);
}

export const SubmitOrderProvider: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const { cloverInstance } = useCloverPaymentIframeContext();
  const { attemptReCaptcha, isReady: isReCaptchaReady, locale: reCaptchaLocale } = useReCaptcha();
  const { merchant } = useMerchant();
  const { isAcceptingOrders, showDineInState, showRunningOrderState } = useMerchantState();
  const { items: cartItems, clear: clearCartItems } = useCart();
  const { items: giftCards, clear: clearGiftCards } = useGiftCardCart();
  const { invoiceSummary, selectedPerk, setPostOrder, setConversionTrackingResponse } = useInvoiceSummary();
  const { contactFormData: { customer }, noteFormData: { note }, tipValue } = useOrderDetails();
  const navigate = useNavigate();
  const { isApplePayEnabled, payWithApplePay } = useApplePay();
  const { setOrderResponse } = useOrderResponseSummary();
  const { currentServiceType, typeMetadata, resetServiceTypeData, note: merchantNote, tableDetails, isDelivery } = useServiceType();
  const { currentDate, currentTime } = useScheduledOrderDisplay();
  const { scheduledTime } = useScheduledOrderMethods();
  const { customer: globalCustomer, sessionToken, chosenMarketingAllowed } = useCustomer();
  const { deliveryAddress: address, pickupTime, deliveryTime } = useDelivery();
  const postOrdersImpl = usePostOrdersRequest(merchant?.merchantUuid, sessionToken);
  const postTableOrdersImpl = useTableOrderPaymentsRequest(merchant?.merchantUuid, sessionToken);
  const { mutateAsync: putTableOrderImpl } = usePutTableOrders(merchant?.merchantUuid, tableDetails?.id);
  const timezone = merchant?.timezone || 'local';
  const totalTime= `${formatTimeForDelivery(pickupTime, merchant?.leadTime, timezone, timeFormat.simple)} - ${formatTimeForDelivery(deliveryTime, merchant?.leadTime, timezone, timeFormat.simple)}`;
  const { appliedGiftCards, clearGiftCard:clearAppliedGiftCard } = useGiftCard();
  const [isCheckoutCalledFromClearGiftCard, setIsCheckoutCalledFromClearGiftCard] = useState(false);
  const sessionProviderType = SessionStorage.tryGetItem(QUERY_PARAMS.utmSource);
  const providerType = sessionProviderType === OnlineOrderProviders.OWG ? OnlineOrderProviders.OWG : OnlineOrderProviders.CWO;
  const [state, setState] = useState<StoredState>({
    isOrderProcessing: false
  });

  // validate if checkout API is successful & then clear previous submit order error
  useEffect(() => {
    if (errorMessage && !isCheckoutCalledFromClearGiftCard ) {
      setState({
        error: undefined,
        errorMessage: undefined,
        isOrderProcessing: false
      });
    }
    setIsCheckoutCalledFromClearGiftCard(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceSummary]);

  useEffect(() => {
    if (queryString.parse(window.location.search).error) {
      setState({
        error: getI18nKeyFromError(undefined),
        isOrderProcessing: false
      });
    }
  }, [setState]);

  const isIpg = useMemo(() => {
    return isPaymentProcessorIpg(merchant?.paymentMethods);
  }, [merchant]);

  const estimatedTime = useMemo(() => {
    if (currentDate && currentTime) {
      return t('success.scheduled_order', { date: currentDate?.label, time: currentTime?.label });
    }

    if (isDelivery) {
      return t('success.asap_delivery_time', { minutes: totalTime });
    }

     return t('success.lead_time', { minutes: merchant?.leadTime });
  }, [currentDate, currentTime, isDelivery, merchant?.leadTime, t, totalTime]);

  const redirectToStatus = useCallback((receiptId: string, deliveryTrackingUrl?: string) => {
    // TODO support more than one gift card
    const { recipient } = (giftCards[0] ?? {}) as Partial<GiftCardItem>;

    navigate(`/${CLIENT_PATH}/${merchant?.slug}/status`, {
      replace: true,
      state: {
        receiptId,
        giftCardRecipientEmail: (recipient && (!recipient.isCustomer && recipient.email)) || undefined,
        hasGiftCardsOnly: cartItems.length === 0 && giftCards.length > 0,
        orderItems: showDineInState ? cartItems : undefined,
        estimatedTime,
        merchantNote,
        deliveryTrackingUrl,
      },
    });

    clearCartItems();
    clearGiftCards();
    resetServiceTypeData();
    // Clear Contact Info
    SessionStorage.tryRemoveItem(COLO_CONTACT_INFO);
    SessionStorage.tryRemoveItem(COLO_CONTACT_NOTE);
  }, [giftCards, navigate, merchant?.slug, cartItems, showDineInState, estimatedTime, merchantNote, clearCartItems, clearGiftCards, resetServiceTypeData]);

  const postOrders = useCallback(async (paymentToken?: string) => {
    reCaptchaReadyLogger(isReCaptchaReady);

    try {
      const reCaptchaToken = await attemptReCaptcha();

      if (showRunningOrderState){
        return await postTableOrdersImpl({
          tableUuid: tableDetails?.id,
          ...(!!paymentToken && { paymentToken }),
          tipValue,
          customer,
          captchaToken: reCaptchaToken,
          appliedGiftCards,
        });
      } else {

        return await postOrdersImpl({
          globalCustomerUuid: globalCustomer?.globalCustomerUuid,
          customer: {
            ...customer,
            marketingAllowed: chosenMarketingAllowed,
          },
          note,
          cartItems: (isAcceptingOrders ? cartItems : []),
          giftCards,
          tipValue,
          ...(!!paymentToken && { paymentToken }),
          type: currentServiceType,
          perkUuid: selectedPerk,
          typeMetadata,
          scheduledTime,
          appliedGiftCards,
          address,
          providerType,
        }, reCaptchaToken);
      }
    } catch (e) {
      invalidateSession(e);
      setState({
        isOrderProcessing: false,
        error: getI18nKeyFromError(e, cartItems),
        errorMessage: parseErrorMessage(e)
      });
      setPostOrder?.(true);
      clearAppliedGiftCard();
      setIsCheckoutCalledFromClearGiftCard(true);
      console.error('Checkout failed', e);
    }
  }, [
    isReCaptchaReady,
    attemptReCaptcha,
    showRunningOrderState,
    postTableOrdersImpl,
    tableDetails?.id,
    postOrdersImpl,
    globalCustomer?.globalCustomerUuid,
    customer,
    chosenMarketingAllowed,
    note,
    isAcceptingOrders,
    cartItems,
    giftCards,
    tipValue,
    currentServiceType,
    typeMetadata,
    scheduledTime,
    selectedPerk,
    address,
    setPostOrder,
    appliedGiftCards,
    clearAppliedGiftCard,
    providerType
  ]);

  const submitTableOrder = useCallback(async () => {
    reCaptchaReadyLogger(isReCaptchaReady);

    try {
      const reCaptchaToken = await attemptReCaptcha();

      setState({ isOrderProcessing: true });

      await putTableOrderImpl({
        request:{
          cartItems,
          giftCards,
          globalCustomerUuid: globalCustomer?.globalCustomerUuid,
          note,
          tipValue,
          typeMetadata,
        },
        captchaToken: reCaptchaToken
      });

      heapTrack('S2O-cart-click-send-items-to-kitchen');
      clearCartItems();
      clearGiftCards();

    } catch (e) {
      setState({
        isOrderProcessing: false,
        error: getI18nKeyFromError(e, cartItems),
      });

      console.error('Table order failed', e);
      throw getI18nKeyFromError(e);
    } finally {
      setState((prev) => ({ ...prev, isOrderProcessing: false }));
    }
  }, [
    attemptReCaptcha,
    cartItems,
    clearCartItems,
    clearGiftCards,
    giftCards,
    globalCustomer?.globalCustomerUuid,
    isReCaptchaReady,
    note,
    putTableOrderImpl,
    tipValue,
    typeMetadata,
  ]);

  const createCloverPaymentToken = useCallback(async () => {
    // technically not possible; based on how we used it
    // but need guard condition for typescript
    if (!cloverInstance) throw new Error(t('orderingUnavailable'));

    try {
      const cToken = await cloverInstance.createToken();

      if (!cToken) {
        return;
      }

      return cToken;
    } catch (e) {
      console.error('Payment token could not be created');
    }
  }, [cloverInstance, t]);

  const submitWithCloverPay = useDebounce(async (totalWithTip:number) => {
    setState({ isOrderProcessing: true });

    try {
      let response;
      if (totalWithTip === 0 && appliedGiftCards.length >0){
        response = await postOrders();
      } else {
        const cToken = await createCloverPaymentToken();
        if (!cToken?.token) {
          setState((prev) => ({ ...prev, error: getI18nKeyFromError(undefined) }));

          return;
        }
        response = await postOrders(cToken.token);
      }
      if (!response) return;
      setConversionTrackingResponse?.(response);
      const { receiptId, deliveryTrackingUrl } = response;
      redirectToStatus(receiptId, deliveryTrackingUrl);
    } finally {
      setState(prev => ({ ...prev, isOrderProcessing: false }));
    }
  }, 250, [createCloverPaymentToken, redirectToStatus, postOrders]);

  const submitWithGooglePay = useDebounce(async (tokenResponse: Clover.HasToken) => {
    setState({ isOrderProcessing: true });
    const response = await postOrders(tokenResponse.token);

    if (!response) return;
    setConversionTrackingResponse?.(response);

    const { receiptId, deliveryTrackingUrl } = response;
    redirectToStatus(receiptId, deliveryTrackingUrl);
   }, 250, [postOrders]);

   const completeApplePayPayment = useDebounce(async (
      session: ApplePaySession,
      receiptId: string,
      tipValue: number,
      tableUuid?: string,
      customer?: Customer,
      captchaToken?: string,
      response?: OrderReceipt) => {

        try {
          const possibleOrderReceipt = await payWithApplePay(session, receiptId, tipValue, tableUuid, customer, captchaToken);

          if (!possibleOrderReceipt) {
            setState((prev) => ({ ...prev, error: getI18nKeyFromError(undefined) }));

            return;
          }

          redirectToStatus(receiptId, possibleOrderReceipt?.deliveryTrackingUrl);

        } catch (e) {
          setState((prev) => ({ ...prev, error: getI18nKeyFromError(undefined) }));
        }
   }, [payWithApplePay, redirectToStatus]);

  const processWithApplePay = useDebounce(async (session: ApplePaySession) => {
    setState({ isOrderProcessing: true });

    try {
      const orderId = tableDetails?.order?.id;

      if (showRunningOrderState && orderId) {
        const reCaptchaToken = await attemptReCaptcha();

        await completeApplePayPayment(session, orderId, tipValue, tableDetails.id, customer, reCaptchaToken);

        return;
      }

      const response = await postOrders(OLO_APPLE_PAY_TOKEN);

      if (!response) return;

      const { receiptId } = response;
      setConversionTrackingResponse?.(response);
      await completeApplePayPayment(session, receiptId, tipValue, undefined, undefined, undefined, response);

    } catch (e) {
      setState((prev) => ({ ...prev, error: getI18nKeyFromError(undefined) }));

      console.error(e);
    } finally {
      setState(prev => ({ ...prev, isOrderProcessing: false }));
    }
  }, [postOrders, tipValue, payWithApplePay, redirectToStatus]);

  const createSession = useCreateApplePaySession();

  const submitWithApplePay = useCallback(async () => {
    if (!isApplePayEnabled || !invoiceSummary) return;

    return processWithApplePay(createSession({
      shippingMethods: showDineInState ? undefined : [
        {
          label:  t('checkoutPage.shippingMethods.pickup'),
          detail: t('checkoutPage.shippingMethods.pickup_in', { count: merchant?.leadTime }),
          amount: '0.00',
          identifier: 'PICKUP',
        }
      ]
    }));
  }, [createSession, invoiceSummary, isApplePayEnabled, merchant, processWithApplePay, t, showDineInState]);

  const submitWithIpgPay = useDebounce(async () => {
    setState({ isOrderProcessing: true });

    try {
      const response = await postOrders(OLO_IPG_TOKEN);
      setOrderResponse(response);
      if (response){
        setConversionTrackingResponse?.(response);
      }
    } catch (e) {
      console.error(e);
    }
  }, [postOrders, isReCaptchaReady]);

  const { error, errorMessage, isOrderProcessing } = state;

  const hasUnrecoverableError = useMemo(() => {
    return error === GYFT_ERROR_CODE;
  }, [error]);

  return (
    <SubmitOrderContext.Provider
      value={{
        error,
        errorMessage,
        isOrderProcessing,
        setState,
        submit: isIpg ? submitWithIpgPay : submitWithCloverPay,
        submitWithApplePay,
        submitWithGooglePay,
        submitTableOrder,
        hasUnrecoverableError,
        isReCaptchaReady,
        reCaptchaLocale }}>
      { isIpg && <IpgPaymentForm estimatedTime={estimatedTime} />}
      {children}
    </SubmitOrderContext.Provider>
  );
};
